// ============================================================================
// <index-portal> — role-selector landing page.
//
// Reads progress from every known track's localStorage and renders one card
// per role. Active tracks link to the track page; future roles render as
// disabled "coming soon" cards.
// ============================================================================

import { LitElement, html, css, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { localStorageAdapter, type TrackConfig } from '../engine/index.js';
import { brandStyles } from '../components/shared-styles.js';

export interface RoleCard {
  /** Stable identifier — used for the `id` attribute */
  id: string;
  /** Display name */
  title: string;
  /** Short description shown on the card */
  description: string;
  /** Where the card links to (e.g. `maintenance.html`) */
  href?: string;
  /** When this card is active, the track config to read progress from */
  track?: TrackConfig;
  /** "Coming soon" cards are dimmed and non-clickable */
  status: 'active' | 'coming_soon';
}

@customElement('index-portal')
export class IndexPortal extends LitElement {
  static override styles = [
    brandStyles,
    css`
      :host {
        display: block;
        min-height: 100vh;
        background: var(--av-black);
        color: var(--av-white);
        font-family: var(--font);
      }
      .portal {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }

      header.top {
        padding: 28px 48px 20px;
        border-bottom: 2px solid var(--av-gold);
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 12px;
        flex-wrap: wrap;
      }
      .wordmark { display: flex; flex-direction: column; gap: 2px; }
      .wm-name { font-size: 26px; font-weight: 700; letter-spacing: 0.16em; color: var(--av-white); }
      .wm-group {
        font-size: 10px;
        font-weight: 400;
        letter-spacing: 0.35em;
        color: var(--av-silver);
        text-transform: uppercase;
      }
      .wm-promise { font-size: 10px; color: var(--av-gold); letter-spacing: 0.12em; margin-top: 3px; }

      .top-actions { display: flex; gap: 10px; align-items: center; }
      .header-badge {
        font-size: 10px;
        letter-spacing: 0.2em;
        text-transform: uppercase;
        color: var(--av-silver);
        border: 1px solid #222;
        padding: 5px 12px;
        border-radius: 2px;
      }
      .admin-link {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        color: var(--av-silver);
        text-decoration: none;
        border: 1px solid #222;
        padding: 5px 12px;
        border-radius: 2px;
        transition: color 0.2s, border-color 0.2s;
      }
      .admin-link:hover { color: var(--av-gold); border-color: rgba(208,151,61,0.4); }

      section.hero { text-align: center; padding: 60px 48px 44px; }
      .hero-eyebrow {
        font-size: 10px;
        letter-spacing: 0.35em;
        text-transform: uppercase;
        color: var(--av-silver);
        margin-bottom: 14px;
      }
      .hero-title {
        font-size: 34px;
        font-weight: 700;
        color: var(--av-white);
        margin-bottom: 14px;
        letter-spacing: 0.02em;
      }
      .hero-sub {
        font-size: 15px;
        color: var(--av-silver);
        max-width: 540px;
        margin: 0 auto 10px;
        line-height: 1.65;
      }
      .hero-regs { font-size: 11px; color: #444; letter-spacing: 0.08em; }

      main.roles-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
        max-width: 960px;
        margin: 0 auto;
        padding: 0 48px 60px;
        flex: 1;
        width: 100%;
        box-sizing: border-box;
      }

      .role-card {
        background: #111;
        border: 1px solid #222;
        border-radius: 4px;
        padding: 28px;
        text-decoration: none;
        color: var(--av-white);
        display: flex;
        flex-direction: column;
        transition: border-color 0.2s, transform 0.15s;
      }
      .role-card.active:hover {
        border-color: var(--av-gold);
        transform: translateY(-2px);
      }
      .role-card.disabled { opacity: 0.45; cursor: default; pointer-events: none; }
      .card-accent { width: 28px; height: 3px; background: var(--av-gold); margin-bottom: 18px; }
      .card-title { font-size: 17px; font-weight: 700; margin-bottom: 7px; }
      .card-desc {
        font-size: 13px;
        color: var(--av-silver);
        line-height: 1.55;
        margin-bottom: 22px;
        flex: 1;
      }
      .card-progress { margin-bottom: 18px; }
      .progress-row {
        display: flex;
        justify-content: space-between;
        font-size: 10px;
        color: var(--av-silver);
        text-transform: uppercase;
        letter-spacing: 0.1em;
        margin-bottom: 6px;
      }
      .progress-bar { height: 3px; background: #222; border-radius: 2px; overflow: hidden; }
      .progress-fill { height: 100%; background: var(--av-gold); transition: width 0.6s ease; }
      .card-status {
        display: inline-block;
        font-size: 10px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        padding: 4px 10px;
        border-radius: 2px;
        align-self: flex-start;
      }
      .status-active {
        background: rgba(208,151,61,0.12);
        color: var(--av-gold);
        border: 1px solid rgba(208,151,61,0.3);
      }
      .status-soon {
        background: rgba(128,130,133,0.08);
        color: var(--av-silver);
        border: 1px solid rgba(128,130,133,0.18);
      }

      footer.bottom {
        padding: 20px 48px;
        border-top: 1px solid #222;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
      }
      .footer-ethics { font-size: 10px; color: var(--av-silver); letter-spacing: 0.12em; }
      .footer-info   { font-size: 10px; color: #333; }

      @media (max-width: 720px) {
        header.top { padding: 20px 24px 16px; }
        section.hero { padding: 40px 24px 32px; }
        main.roles-grid { padding: 0 24px 40px; grid-template-columns: 1fr; }
        footer.bottom { padding: 16px 24px; flex-direction: column; gap: 8px; text-align: center; }
        .hero-title { font-size: 26px; }
      }
    `,
  ];

  /** Injected by main-index.ts. */
  @property({ attribute: false }) cards: RoleCard[] = [];

  @state() private progressByTrack: Record<string, { done: number; total: number; pct: number }> = {};

  override connectedCallback() {
    super.connectedCallback();
    // Re-read when the page becomes visible (a learner may have advanced
    // their progress in another tab/window).
    document.addEventListener('visibilitychange', this.refreshProgress);
  }

  override disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.refreshProgress);
    super.disconnectedCallback();
  }

  override willUpdate(changed: PropertyValues<this>) {
    // `cards` is injected by the entry script after element upgrade, so the
    // first refresh must wait until we have them. Subsequent updates also
    // re-read in case the cards list changes (e.g. a new role unlocks).
    if (changed.has('cards')) this.refreshProgress();
  }

  private refreshProgress = () => {
    const map: typeof this.progressByTrack = {};
    for (const card of this.cards) {
      if (!card.track) continue;
      const adapter = localStorageAdapter(card.track.storageKey);
      const learner = adapter.load();
      const done = learner.progress.completedModules.length;
      const total = card.track.moduleOrder.length;
      const pct = total ? Math.round((done / total) * 100) : 0;
      map[card.id] = { done, total, pct };
    }
    this.progressByTrack = map;
  };

  override render() {
    return html`
      <div class="portal">
        <header class="top">
          <div class="wordmark">
            <span class="wm-name">AVCAIR</span>
            <span class="wm-group">Aviation Group</span>
            <span class="wm-promise">aviation . excellence</span>
          </div>
          <div class="top-actions">
            <span class="header-badge">Internal Training Platform</span>
            <a href="admin.html" class="admin-link">⚙ Safety Manager</a>
          </div>
        </header>

        <section class="hero">
          <p class="hero-eyebrow">Select your role to begin</p>
          <h1 class="hero-title">Human Factors Training Program</h1>
          <p class="hero-sub">
            Evidence-based human factors training developed for AVCAIR staff.
            Complete at your own pace — progress is saved automatically.
          </p>
          <p class="hero-regs">
            CASR Part 145 · Part 139 · CAO 82.3/82.5 · IATA IGOM · IS-BAH aligned
          </p>
        </section>

        <main class="roles-grid">
          ${this.cards.map((c) => this.renderCard(c))}
        </main>

        <footer class="bottom">
          <span class="footer-ethics">exclusive | reliable | certainty</span>
          <span class="footer-info">AVCAIR Human Factors Training Program · v2.0 · 2026</span>
        </footer>
      </div>
    `;
  }

  private renderCard(card: RoleCard) {
    const isActive = card.status === 'active';
    const prog = card.track ? this.progressByTrack[card.id] : undefined;
    const total = prog?.total ?? card.track?.moduleOrder.length ?? 0;
    const done = prog?.done ?? 0;
    const pct = prog?.pct ?? 0;

    const inner = html`
      <div class="card-accent"></div>
      <div class="card-title">${card.title}</div>
      <div class="card-desc">${card.description}</div>
      ${isActive && card.track
        ? html`<div class="card-progress">
            <div class="progress-row">
              <span>Progress</span>
              <span>${done} of ${total} modules</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" style="width:${pct}%"></div>
            </div>
          </div>`
        : html`<div class="card-progress">
            <div class="progress-row"><span>Progress</span><span>—</span></div>
            <div class="progress-bar"><div class="progress-fill" style="width:0%"></div></div>
          </div>`}
      <span class="card-status ${isActive ? 'status-active' : 'status-soon'}">
        ${isActive ? 'Available now' : 'Coming soon'}
      </span>
    `;

    if (isActive && card.href) {
      return html`<a class=${classMap({ 'role-card': true, active: true })} href=${card.href}>
        ${inner}
      </a>`;
    }
    return html`<div class=${classMap({ 'role-card': true, disabled: true })}>${inner}</div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'index-portal': IndexPortal;
  }
}
